import { useAuth } from '../contexts/AuthContext';
import { boardPreferenceKey, preferredBoard, rememberBoard } from '../lib/boardPreference';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type { LoadingState, Job, Board, BoardStatus, JobDetail, Photo, Document } from '../types';
import type { Task } from '../types/tasks';

export interface UseJobsOptions {
  includeCompleted?: boolean;
}

export interface UseJobsResult {
  jobs: Job[];
  boards: Board[];
  statuses: BoardStatus[];
  loading: LoadingState;
  selectedBoard: number | null;
  setSelectedBoard: (boardId: number) => void;
  updateJobStatus: (jobId: number, statusId: number) => Promise<void>;
  markJobComplete: (jobId: number, isCompleted?: boolean) => Promise<void>;
  createJob: (job: Partial<Job>) => Promise<void>;
  updateJob: (jobId: number, job: Partial<Job>) => Promise<void>;
  deleteJob: (jobId: number) => Promise<void>;
  refreshData: () => Promise<void>;
}

export const useJobs = (options: UseJobsOptions = {}): UseJobsResult => {
  const { includeCompleted = false } = options;
  const [jobs, setJobs] = useState<Job[]>([]);
  const [boards, setBoards] = useState<Board[]>([]);
  const [statuses, setStatuses] = useState<BoardStatus[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<number | null>(null);
  const { user } = useAuth();
  const preferenceKey = boardPreferenceKey('jobs', user);
  const boardsVersion = useRef(0);
  const [loading, setLoading] = useState<LoadingState>({
    isLoading: true,
    error: null,
  });

  const requestVersion = useRef(0);
  useEffect(
    () => () => {
      requestVersion.current += 1;
      boardsVersion.current += 1;
    },
    [],
  );

  const handleError = (error: any) => {
    setLoading({
      isLoading: false,
      error: error.message || 'An unexpected error occurred',
    });
  };

  const loadBoards = useCallback(async () => {
    const version = ++boardsVersion.current;
    requestVersion.current += 1;
    setSelectedBoard(null);
    setBoards([]);
    setStatuses([]);
    setJobs([]);
    try {
      const response = await apiClient.getBoards();
      const available = response.data;
      if (version !== boardsVersion.current) return;
      setBoards(available);
      setSelectedBoard(preferredBoard(available, preferenceKey));
      if (!available.length) setLoading({ isLoading: false, error: null });
    } catch (error) {
      if (version === boardsVersion.current) handleError(error);
    }
  }, [preferenceKey]);

  const loadKanbanData = useCallback(async (boardId: number) => {
    const version = ++requestVersion.current;
    try {
      setLoading({ isLoading: true, error: null });

      const data = await apiClient.getKanbanData(boardId, includeCompleted);
      if (version !== requestVersion.current) return;
      setJobs(data.jobs);
      setStatuses(data.statuses);

      setLoading({ isLoading: false, error: null });
    } catch (error) {
      if (version === requestVersion.current) handleError(error);
    }
  }, [includeCompleted]);

  const updateJobStatus = useCallback(async (jobId: number, statusId: number) => {
    try {
      await apiClient.updateJobStatus(jobId, statusId);

      // Update the job in the local state
      setJobs((prevJobs) =>
        prevJobs.map((job) => (job.id === jobId ? { ...job, board_status: statusId } : job)),
      );
    } catch (error) {
      handleError(error);
      throw error; // Re-throw to handle in drag-drop
    }
  }, []);

  const markJobComplete = useCallback(async (jobId: number, isCompleted: boolean = true) => {
    try {
      const response = await apiClient.markJobComplete(jobId, isCompleted);
      const updatedJob = response.data;

      // Remove the job from local state when completed (it will be filtered out)
      // or update it when un-completed
      if (isCompleted && !includeCompleted) {
        setJobs(prevJobs => prevJobs.filter(job => job.id !== jobId));
      } else {
        setJobs(prevJobs =>
          prevJobs.map(job =>
            job.id === jobId
              ? {
                  ...job,
                  ...updatedJob,
                  is_completed: isCompleted,
                  completed_at: isCompleted ? updatedJob.completed_at : undefined,
                }
              : job
          )
        );
      }
    } catch (error) {
      handleError(error);
      throw error;
    }
  }, [includeCompleted]);

  const createJob = useCallback(async (jobData: Partial<Job>) => {
    try {
      if (!selectedBoard) {
        throw new Error('No board selected');
      }
      
      const response = await apiClient.createJob({
        ...jobData,
        board: selectedBoard,
      });
      
      setJobs(prevJobs => [...prevJobs, response.data]);
    } catch (error) {
      handleError(error);
      throw error;
    }
  }, [selectedBoard]);

  const updateJob = useCallback(async (jobId: number, jobData: Partial<Job>) => {
    try {
      const response = await apiClient.updateJob(jobId, jobData);
      
      setJobs((prevJobs) =>
        prevJobs.map((job) => (job.id === jobId ? { ...job, ...response.data } : job)),
      );
    } catch (error) {
      handleError(error);
      throw error;
    }
  }, []);

  const deleteJob = useCallback(async (jobId: number) => {
    try {
      await apiClient.deleteJob(jobId);
      
      setJobs(prevJobs => prevJobs.filter(job => job.id !== jobId));
    } catch (error) {
      handleError(error);
      throw error;
    }
  }, []);

  const refreshData = useCallback(async () => {
    if (selectedBoard) {
      await loadKanbanData(selectedBoard);
    }
  }, [selectedBoard, loadKanbanData]);

  const handleBoardChange = useCallback(
    (boardId: number) => {
      if (!boards.some((board) => board.id === boardId)) return;
      rememberBoard(preferenceKey, boardId);
    requestVersion.current += 1;
    setJobs([]);
    setStatuses([]);
    setLoading({ isLoading: true, error: null });
    setSelectedBoard(boardId);
    },
    [boards, preferenceKey],
  );

  // Load initial data
  useEffect(() => {
    loadBoards();
  }, [loadBoards]);

  // Load kanban data when selected board or includeCompleted changes
  useEffect(() => {
    if (selectedBoard) {
      loadKanbanData(selectedBoard);
    }
  }, [selectedBoard, loadKanbanData, includeCompleted]);

  return {
    jobs,
    boards,
    statuses,
    loading,
    selectedBoard,
    setSelectedBoard: handleBoardChange,
    updateJobStatus,
    markJobComplete,
    createJob,
    updateJob,
    deleteJob,
    refreshData,
  };
};

// React Query hooks for Job Detail functionality
export const useJobDetail = (jobId: string | undefined) => {
  return useQuery({
    queryKey: ['job', jobId],
    queryFn: async (): Promise<JobDetail> => {
      if (!jobId) throw new Error('Job ID is required');
      const response = await apiClient.get<JobDetail>(`/api/jobs/${jobId}/`);
      return response.data;
    },
    enabled: !!jobId,
  });
};

export const useJobPhotos = (jobId: string | undefined) => {
  return useQuery({
    queryKey: ['jobPhotos', jobId],
    queryFn: async (): Promise<Photo[]> => {
      if (!jobId) throw new Error('Job ID is required');
      // `?page_size=1000` was clamped to the view's max_page_size of 100, so a
      // job with more than 100 photos silently rendered only the first 100 —
      // no error, no "load more", just missing photos.
      const response = await apiClient.getAllPages<Photo>(`/api/photos/by-job/${jobId}/`);
      return response.data;
    },
    enabled: !!jobId,
  });
};

export const useJobTasks = (jobId: string | undefined) => {
  return useQuery({
    queryKey: ['jobTasks', jobId],
    queryFn: async (): Promise<Task[]> => {
      if (!jobId) throw new Error('Job ID is required');
      const response = await apiClient.get<any>(`/api/tasks/?related_job=${jobId}`);
      return Array.isArray(response.data) ? response.data : response.data.results || [];
    },
    enabled: !!jobId,
    retry: (failureCount, error: any) => {
      // Don't retry for 404 errors
      if (error?.status === 404) return false;
      return failureCount < 3;
    },
  });
};

export const useJobDocs = (jobId: string | undefined) => {
  return useQuery({
    queryKey: ['jobDocs', jobId],
    queryFn: async (): Promise<Document[]> => {
      if (!jobId) throw new Error('Job ID is required');
      // Worse than the photos case: /api/docs/ uses the project-default
      // paginator, which has no page_size query param at all, so this request
      // was capped at PAGE_SIZE = 20 documents.
      const response = await apiClient.getAllPages<Document>(`/api/docs/?job=${jobId}`);
      return response.data;
    },
    enabled: !!jobId,
  });
};

export const useUpdateJobDoc = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ docId, tagNames }: { docId: number; tagNames: string }) => {
      const response = await apiClient.updateDoc(docId, { tag_names: tagNames });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all job docs queries to refresh the documents list
      queryClient.invalidateQueries({ queryKey: ['jobDocs'] });
    },
  });
};

export const useDeleteJobDoc = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (docId: number) => {
      const response = await apiClient.deleteDoc(docId);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all job docs queries to refresh the documents list
      queryClient.invalidateQueries({ queryKey: ['jobDocs'] });
    },
  });
};

export const useBoardsWithStatuses = () => {
  return useQuery({
    queryKey: ['boards-with-statuses'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/api/boards/with-statuses/');
        // Safely handle the response structure
        const data = (response.data as any) || {};
        // Ensure boards is always an array
        let boards: any[] = [];
        if (Array.isArray(data.boards)) {
          boards = data.boards;
        } else if (Array.isArray(data)) {
          boards = data;
        } else {
          // If data is not in expected format, return empty array
          console.warn('Unexpected response format from boards/with-statuses:', data);
          boards = [];
        }
        
        // Ensure each board has statuses array
        boards = boards.map((board: any) => ({
          ...board,
          statuses: Array.isArray(board.statuses) ? board.statuses : []
        }));
        
        return { boards };
      } catch (error: any) {
        // Handle authentication errors specifically
        if (error?.response?.status === 401) {
          console.warn('Authentication required for boards/with-statuses. User may need to log in.');
        } else {
          console.error('Error fetching boards with statuses:', error);
        }
        // Always return safe default structure
        return { boards: [] };
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes since boards/statuses don't change often
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.response?.status === 401) return false;
      return failureCount < 1;
    },
    refetchOnWindowFocus: false, // Prevent refetch on window focus to reduce 401 errors
  });
};

// Job contact management mutations
export const useAddJobContact = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ jobId, contactId }: { jobId: string; contactId: number }) => {
      const response = await apiClient.post(`/api/jobs/${jobId}/add-contact/`, {
        contact_id: contactId
      });
      return response.data;
    },
    onSuccess: (_, { jobId }) => {
      // Invalidate the job detail query to refresh the contacts list
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
    },
  });
};

export const useRemoveJobContact = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ jobId, contactId }: { jobId: string; contactId: number }) => {
      const response = await apiClient.post(`/api/jobs/${jobId}/remove-contact/`, {
        contact_id: contactId
      });
      return response.data;
    },
    onSuccess: (_, { jobId }) => {
      // Invalidate the job detail query to refresh the contacts list
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
    },
  });
};

// Photo upload mutation
export const useUploadPhotos = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ jobId, files, description }: { 
      jobId: string; 
      files: File[]; 
      description?: string; 
    }) => {
      const response = await apiClient.uploadPhotos(parseInt(jobId), files, description);
      return response.data;
    },
    onSuccess: (_, { jobId }) => {
      // Invalidate the job photos query to refresh the photos list
      queryClient.invalidateQueries({ queryKey: ['jobPhotos', jobId] });
    },
  });
};

// CoC Request hooks
export const useCoCRequests = (jobId?: string) => {
  return useQuery({
    queryKey: ['coc-requests', jobId],
    queryFn: async () => {
      const response = await apiClient.get(`/api/coc-requests/?job_id=${jobId}`);
      const data = response.data as any;
      return data.results || data;
    },
    enabled: !!jobId,
  });
};

export const useCreateCoCRequest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      jobId, 
      total_contract_amount, 
      depreciation_due 
    }: { 
      jobId: string; 
      total_contract_amount: string; 
      depreciation_due: string; 
    }) => {
      const response = await apiClient.post(`/api/jobs/${jobId}/coc-request/`, {
        total_contract_amount,
        depreciation_due,
      });
      return response.data;
    },
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ['coc-requests', jobId] });
    },
  });
};

export const useSendCoCEmail = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      requestId, 
      email_subject, 
      email_body, 
      recipient_email 
    }: { 
      requestId: number; 
      email_subject: string; 
      email_body: string; 
      recipient_email: string; 
    }) => {
      const response = await apiClient.post(`/api/coc-requests/${requestId}/send/`, {
        email_subject,
        email_body,
        recipient_email,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coc-requests'] });
    },
  });
};